import React, { useState } from "react";
import logo from "../assets/lna.jpg";

const Advocacy = () => {
  const initiatives = [
    {
      id: 1,
      title: "Advocating for Nurses' Rights",
      description: "We represent nurses in both public and private sectors by negotiating fair compensation, improved working conditions, and enhanced professional recognition.",
      link: "/advocacy/nurses-rights",
      icon: "⚖️"
    },
    {
      id: 2,
      title: "Representation in Local & International Forums",
      description: "We ensure nurses have a voice at key national and international forums, promoting best practices and setting industry standards in healthcare delivery.",
      link: "/advocacy/representation",
      icon: "🌍"
    },
    {
      id: 3,
      title: "Policy Influence & Collective Bargaining",
      description: "Our organization actively engages with policymakers to influence healthcare policy, ensuring a safe and supportive environment for nurses and their patients.",
      link: "/advocacy/policy-influence",
      icon: "📊"
    },
  ];

  const successStories = [
    {
      id: 1,
      title: "Collective Bargaining Agreements",
      description: "Through successful negotiations, we've secured better employment contracts, ensuring job security, competitive salaries, and improved healthcare benefits.",
      link: "/success-stories/collective-bargaining",
      metrics: "15% average salary increase"
    },
    {
      id: 2,
      title: "Nursing Leadership Recognition",
      description: "Our advocacy efforts have led to increased recognition of nurses' leadership roles in both healthcare management and public health policy development.",
      link: "/success-stories/leadership-recognition",
      metrics: "200+ leadership positions secured"
    },
  ];

  const [activeCard, setActiveCard] = useState(null);

  const handleCardInteraction = (cardId) => setActiveCard(cardId);
  const clearActiveCard = () => setActiveCard(null);

  const Card = ({ item, index, section, isActive, onInteract, onLeave }) => (
    <li 
      className={`advocacy-card ${isActive ? 'advocacy-card--active' : ''} ${section === 'success' ? 'advocacy-card--success' : ''}`}
      onMouseEnter={() => onInteract(item.id)}
      onMouseLeave={onLeave}
      onFocus={() => onInteract(item.id)}
      onBlur={onLeave}
      onTouchStart={() => onInteract(item.id)}
      onTouchEnd={onLeave}
    >
      <div className="advocacy-card__header">
        {item.icon && <span className="advocacy-card__icon" aria-hidden="true">{item.icon}</span>}
        <h3 className="advocacy-card__title">{item.title}</h3>
      </div>
      
      <p className="advocacy-card__description">{item.description}</p>
      
      {item.metrics && (
        <div className="advocacy-card__metrics">
          <strong>{item.metrics}</strong>
        </div>
      )}
      
      <a 
        href={item.link}
        className="advocacy-card__link"
        aria-label={`Learn more about ${item.title}`}
      >
        {section === 'success' ? 'Read Full Story' : 'Learn More'}
      </a>
    </li>
  );

  return (
    <div className="advocacy">
      <header className="advocacy__header">
        <img 
          src={logo} 
          alt="Lesotho Nurses Association - Advocating for Healthcare Excellence" 
          className="advocacy__logo"
        />
        <h1 className="advocacy__title">Advocacy and Policy</h1>
        <p className="advocacy__intro">
          Championing the rights and recognition of nurses through strategic advocacy, 
          policy influence, and collective action.
        </p>
      </header>

      <main className="advocacy__content">
        <section className="advocacy__section" aria-labelledby="initiatives-heading">
          <div className="advocacy__section-header">
            <h2 id="initiatives-heading" className="advocacy__section-title">
              Our Advocacy Initiatives
            </h2>
            <p className="advocacy__section-description">
              Driving meaningful change through targeted advocacy programs and strategic partnerships
            </p>
          </div>
          
          <ul className="advocacy__grid">
            {initiatives.map((initiative) => (
              <Card
                key={initiative.id}
                item={initiative}
                section="initiatives"
                isActive={activeCard === initiative.id}
                onInteract={handleCardInteraction}
                onLeave={clearActiveCard}
              />
            ))}
          </ul>
        </section>

        <section className="advocacy__section" aria-labelledby="success-heading">
          <div className="advocacy__section-header">
            <h2 id="success-heading" className="advocacy__section-title">
              Success Stories
            </h2>
            <p className="advocacy__section-description">
              Celebrating our achievements in advancing nursing profession and healthcare standards
            </p>
          </div>
          
          <ul className="advocacy__grid">
            {successStories.map((story) => (
              <Card
                key={story.id}
                item={story}
                section="success"
                isActive={activeCard === story.id}
                onInteract={handleCardInteraction}
                onLeave={clearActiveCard}
              />
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
};

// CSS (should be in a separate .css file)
const styles = `
.advocacy {
  padding: 2rem 1rem;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  line-height: 1.6;
  background: linear-gradient(135deg, #f8fbff 0%, #e9f2fb 100%);
  min-height: 100vh;
  max-width: 1200px;
  margin: 0 auto;
}

.advocacy__header {
  text-align: center;
  margin-bottom: 3rem;
}

.advocacy__logo {
  width: 160px;
  height: auto;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.advocacy__title {
  font-size: clamp(2.25rem, 5vw, 3.5rem);
  color: #001f54;
  margin-bottom: 1rem;
  font-weight: 800;
  line-height: 1.2;
}

.advocacy__intro {
  font-size: 1.25rem;
  color: #2d3748;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0.9;
}

.advocacy__content {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.advocacy__section {
  background: #ffffff;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.advocacy__section-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.advocacy__section-title {
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  color: #001f54;
  margin-bottom: 1rem;
  font-weight: 700;
  position: relative;
}

.advocacy__section-title::after {
  content: '';
  position: absolute;
  bottom: -0.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(135deg, #0073e6, #0059b3);
  border-radius: 2px;
}

.advocacy__section-description {
  font-size: 1.125rem;
  color: #4a5568;
  max-width: 500px;
  margin: 0 auto;
}

.advocacy__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  list-style: none;
  padding: 0;
  margin: 0;
}

.advocacy-card {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.advocacy-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #0073e6, #0059b3);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.advocacy-card--success::before {
  background: linear-gradient(135deg, #ff7f50, #d4380d);
}

.advocacy-card:hover,
.advocacy-card--active {
  transform: translateY(-4px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.15);
  border-color: #0073e6;
}

.advocacy-card:hover::before,
.advocacy-card--active::before {
  transform: scaleX(1);
}

.advocacy-card--success:hover,
.advocacy-card--success.advocacy-card--active {
  border-color: #ff7f50;
}

.advocacy-card__header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.advocacy-card__icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.advocacy-card__title {
  font-size: 1.375rem;
  color: #001f54;
  margin: 0;
  font-weight: 700;
  line-height: 1.3;
}

.advocacy-card__description {
  color: #4a5568;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.advocacy-card__metrics {
  background: rgba(0, 115, 230, 0.1);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #0073e6;
}

.advocacy-card--success .advocacy-card__metrics {
  background: rgba(255, 127, 80, 0.1);
  border-left-color: #ff7f50;
}

.advocacy-card__metrics strong {
  color: #001f54;
  font-size: 0.95rem;
}

.advocacy-card__link {
  display: inline-flex;
  align-items: center;
  color: #0073e6;
  font-weight: 600;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border: 2px solid #0073e6;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.advocacy-card__link::after {
  content: '→';
  margin-left: 0.5rem;
  transition: transform 0.3s ease;
}

.advocacy-card:hover .advocacy-card__link,
.advocacy-card--active .advocacy-card__link {
  background: #0073e6;
  color: white;
}

.advocacy-card:hover .advocacy-card__link::after,
.advocacy-card--active .advocacy-card__link::after {
  transform: translateX(4px);
}

.advocacy-card--success .advocacy-card__link {
  color: #ff7f50;
  border-color: #ff7f50;
}

.advocacy-card--success:hover .advocacy-card__link,
.advocacy-card--success.advocacy-card--active .advocacy-card__link {
  background: #ff7f50;
  color: white;
}

/* Responsive Design */
@media (max-width: 768px) {
  .advocacy {
    padding: 1.5rem 1rem;
  }
  
  .advocacy__section {
    padding: 1.5rem;
  }
  
  .advocacy__grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .advocacy-card {
    padding: 1.5rem;
  }
}

/* Focus styles for accessibility */
.advocacy-card__link:focus {
  outline: 3px solid #0073e6;
  outline-offset: 2px;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .advocacy-card,
  .advocacy-card__link {
    transition: none;
  }
}
`;

// Inject styles (in real project, use CSS modules or styled-components)
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default Advocacy;